import { useEffect, useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { useOverlayActivity } from '../../shared/ui/overlay-activity'

import {
  APP_UPDATE_REQUIRED_EVENT,
  hasRequiredAppUpdateRequest,
  requestRequiredAppUpdate,
} from '../../shared/errors/app-update-required'
import { applyAppUpdate } from './update-lifecycle'
import { getAppUpdateStatus, isRequiredMajorUpdate } from './update-policy'
import './pwa-update-prompt.css'

const UPDATE_REMINDER_DELAY_MS = 5 * 60 * 1_000
const UPDATE_CHECK_COOLDOWN_MS = 250
const FOREGROUND_UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1_000

type UpdatePolicy = 'checking' | 'optional' | 'required'

export function PwaUpdatePrompt() {
  const [updateBlocked, setUpdateBlocked] = useState(false)
  const [updatePreparing, setUpdatePreparing] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [updateFailed, setUpdateFailed] = useState(false)
  const [releaseUpdateAvailable, setReleaseUpdateAvailable] = useState(false)
  const [requiredByFailure, setRequiredByFailure] = useState(
    hasRequiredAppUpdateRequest,
  )
  const [updatePolicy, setUpdatePolicy] = useState<UpdatePolicy>('checking')
  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(null)
  const {
    needRefresh: [needRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    // The plugin ignores updateServiceWorker(false)'s reload argument. Keep
    // activation and the single document reload owned by applyAppUpdate.
    onNeedReload: () => undefined,
  })

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return undefined
    }

    let checkInFlight = false
    let lastCheckStartedAt = 0
    let observedRegistration: ServiceWorkerRegistration | undefined
    let observedInstallingWorker: ServiceWorker | undefined
    let disposed = false

    const stopObservingInstallingWorker = () => {
      observedInstallingWorker?.removeEventListener(
        'statechange',
        handleInstallingWorkerStateChange,
      )
      observedInstallingWorker = undefined
    }

    function handleInstallingWorkerStateChange() {
      if (
        observedInstallingWorker?.state === 'installed' &&
        observedRegistration?.waiting
      ) {
        setUpdatePreparing(false)
        setReleaseUpdateAvailable(true)
        stopObservingInstallingWorker()
        return
      }

      if (observedInstallingWorker?.state === 'redundant') {
        setUpdatePreparing(false)
        stopObservingInstallingWorker()
      }
    }

    const handleUpdateFound = () => {
      const installingWorker = observedRegistration?.installing
      if (!navigator.serviceWorker.controller || !installingWorker) return

      stopObservingInstallingWorker()
      observedInstallingWorker = installingWorker
      setUpdatePreparing(true)
      installingWorker.addEventListener(
        'statechange',
        handleInstallingWorkerStateChange,
      )
    }

    const registrationReady = navigator.serviceWorker.ready.then(
      (registration) => {
        if (disposed) return registration
        observedRegistration = registration
        registration.addEventListener('updatefound', handleUpdateFound)
        if (registration.waiting && navigator.serviceWorker.controller) {
          setReleaseUpdateAvailable(true)
        }
        return registration
      },
    )

    const checkForUpdate = () => {
      if (
        disposed ||
        document.visibilityState !== 'visible' ||
        navigator.onLine === false ||
        checkInFlight
      ) {
        return
      }

      const elapsedSinceLastCheck = Date.now() - lastCheckStartedAt
      if (
        lastCheckStartedAt > 0 &&
        elapsedSinceLastCheck < UPDATE_CHECK_COOLDOWN_MS
      ) {
        return
      }

      lastCheckStartedAt = Date.now()
      checkInFlight = true
      void registrationReady
        .then(async (registration) => {
          const [, status] = await Promise.all([
            registration.update().catch(() => undefined),
            getAppUpdateStatus(),
          ])
          if (disposed || !status.available) return

          setReleaseUpdateAvailable(true)
          setUpdatePolicy(status.required ? 'required' : 'optional')
          if (status.required) setSnoozedUntil(null)
        })
        .catch(() => undefined)
        .finally(() => {
          checkInFlight = false
        })
    }

    void registrationReady.then(checkForUpdate)
    document.addEventListener('visibilitychange', checkForUpdate)
    window.addEventListener('focus', checkForUpdate)
    window.addEventListener('online', checkForUpdate)
    window.addEventListener('pageshow', checkForUpdate)
    const intervalId = window.setInterval(
      checkForUpdate,
      FOREGROUND_UPDATE_CHECK_INTERVAL_MS,
    )

    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', checkForUpdate)
      window.removeEventListener('focus', checkForUpdate)
      window.removeEventListener('online', checkForUpdate)
      window.removeEventListener('pageshow', checkForUpdate)
      window.clearInterval(intervalId)
      observedRegistration?.removeEventListener(
        'updatefound',
        handleUpdateFound,
      )
      stopObservingInstallingWorker()
    }
  }, [])

  useEffect(() => {
    const requireUpdate = () => {
      setRequiredByFailure(true)
      setSnoozedUntil(null)
    }
    const handlePreloadError = (event: Event) => {
      event.preventDefault()
      requestRequiredAppUpdate()
    }

    window.addEventListener(APP_UPDATE_REQUIRED_EVENT, requireUpdate)
    window.addEventListener('vite:preloadError', handlePreloadError)
    return () => {
      window.removeEventListener(APP_UPDATE_REQUIRED_EVENT, requireUpdate)
      window.removeEventListener('vite:preloadError', handlePreloadError)
    }
  }, [])

  useEffect(() => {
    if (!needRefresh) return undefined

    const controller = new AbortController()
    void isRequiredMajorUpdate(controller.signal).then((required) => {
      if (controller.signal.aborted) return
      setUpdatePolicy(required ? 'required' : 'optional')
      if (required) setSnoozedUntil(null)
    })

    return () => controller.abort()
  }, [needRefresh])

  useEffect(() => {
    if (snoozedUntil === null) return undefined

    const timeoutId = window.setTimeout(
      () => setSnoozedUntil(null),
      Math.max(0, snoozedUntil - Date.now()),
    )
    return () => window.clearTimeout(timeoutId)
  }, [snoozedUntil])

  const showUpdate =
    needRefresh ||
    requiredByFailure ||
    updatePreparing ||
    releaseUpdateAvailable
  const requiredUpdate = requiredByFailure || updatePolicy === 'required'
  const preparingUpdate = updatePreparing && !needRefresh && !requiredByFailure
  const snoozed = showUpdate && !requiredUpdate && snoozedUntil !== null
  useOverlayActivity(
    Boolean(!snoozed && (requiredUpdate || updating || preparingUpdate)),
  )

  if ((!showUpdate && !offlineReady) || snoozed) return null

  const closeOfflineNotice = () => {
    setOfflineReady(false)
  }

  const remindLater = () => {
    setUpdateBlocked(false)
    setUpdateFailed(false)
    setSnoozedUntil(Date.now() + UPDATE_REMINDER_DELAY_MS)
  }

  const update = async () => {
    setUpdating(true)
    setUpdateBlocked(false)
    setUpdateFailed(false)

    try {
      const didStartUpdate = await applyAppUpdate(updateServiceWorker)
      setUpdateBlocked(!didStartUpdate)
      if (!didStartUpdate) {
        setUpdating(false)
      }
    } catch {
      setUpdating(false)
      setUpdateFailed(true)
    }
  }

  return (
    <aside className="pwa-notice" aria-live="polite" aria-atomic="true">
      <p>
        {preparingUpdate || updating ? (
          <span className="pwa-notice-spinner" aria-hidden="true" />
        ) : null}
        <span>
          {!showUpdate
            ? 'CardReplay готов работать без сети.'
            : preparingUpdate
              ? 'Загружаем новую версию CardReplay'
              : updateBlocked
                ? 'Сначала сохраните изменения или завершите текущее действие, затем повторите обновление.'
                : updateFailed
                  ? 'Не удалось применить обновление. Попробуйте ещё раз.'
                  : updating
                    ? 'Обновляем CardReplay'
                    : requiredUpdate
                      ? 'Доступно важное обновление CardReplay.'
                      : 'Доступна новая версия CardReplay.'}
        </span>
      </p>
      <div className="pwa-notice-actions">
        {!showUpdate ? (
          <button
            type="button"
            onClick={closeOfflineNotice}
            disabled={updating}
          >
            Закрыть
          </button>
        ) : null}
        {showUpdate &&
        !preparingUpdate &&
        !requiredUpdate &&
        updatePolicy === 'optional' ? (
          <button
            type="button"
            data-secondary="true"
            onClick={remindLater}
            disabled={updating}
          >
            Напомнить через 5 минут
          </button>
        ) : null}
        {showUpdate && !preparingUpdate ? (
          <button
            type="button"
            data-primary="true"
            disabled={updating}
            onClick={() => void update()}
          >
            {updating ? 'Обновляем' : 'Обновить'}
          </button>
        ) : null}
      </div>
    </aside>
  )
}
